import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Alert,
  CircularProgress,
  Tooltip,
  Badge
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Sync as SyncIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon
} from '@mui/icons-material';
import { Booking, ExecutionUnit, HealthcareService } from '../../types';
import { bookingService, DeleteOptions } from '../../services/bookingService';
import { executionUnitService } from '../../services/executionUnitService';
import ConfirmationDialog from '../common/ConfirmationDialog';
import DeleteConfirmationDialog from '../DeleteConfirmationDialog';
import SyncConfigDialog from '../SyncConfigDialog';
import './BookingList.css';

interface BookingListProps {
  onEdit?: (booking: Booking) => void;
  onAdd?: () => void;
}

interface BookingFilters {
  search: string;
  status: string;
  executionUnitId: string;
  serviceId: string;
  dateFrom: string;
  dateTo: string;
  patientName: string;
}

interface BookingStats {
  total: number;
  confirmed: number;
  pending: number;
  cancelled: number;
  completed: number;
}

const BookingList: React.FC<BookingListProps> = ({ onEdit, onAdd }) => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [executionUnits, setExecutionUnits] = useState<ExecutionUnit[]>([]);
  const [services, setServices] = useState<HealthcareService[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Filtri e paginazione
  const [filters, setFilters] = useState<BookingFilters>({
    search: '',
    status: '',
    executionUnitId: '',
    serviceId: '',
    dateFrom: '',
    dateTo: '',
    patientName: ''
  });
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  
  // Statistiche
  const [stats, setStats] = useState<BookingStats>({
    total: 0,
    confirmed: 0,
    pending: 0,
    cancelled: 0,
    completed: 0
  });
  
  // Dialog states
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showSyncConfirm, setShowSyncConfirm] = useState(false);
  const [showSyncConfig, setShowSyncConfig] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState<Booking | null>(null);
  
  // Assumiamo che l'utente sia admin se ha il ruolo, altrimenti false
  // In un'app reale, questo verrebbe dal contesto di autenticazione
  const [userIsAdmin] = useState(true); // TODO: sostituire con logica di autenticazione reale

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [bookings, filters]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [bookingsData, unitsData] = await Promise.all([
        bookingService.getBookings(),
        executionUnitService.getExecutionUnits()
      ]);
      
      setBookings(bookingsData);
      setExecutionUnits(unitsData);
      calculateStats(bookingsData);
    } catch (err) {
      setError('Errore nel caricamento dei dati: ' + (err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (bookingData: Booking[]) => {
    const newStats = {
      total: bookingData.length,
      confirmed: bookingData.filter(b => (b.status || b.status_name) === 'confirmed').length,
      pending: bookingData.filter(b => (b.status || b.status_name) === 'pending').length,
      cancelled: bookingData.filter(b => (b.status || b.status_name) === 'cancelled').length,
      completed: bookingData.filter(b => (b.status || b.status_name) === 'completed').length
    };
    setStats(newStats);
  };

  const applyFilters = () => {
    let filtered = [...bookings];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(booking =>
        (booking.patientName || booking.person_name || '')?.toLowerCase().includes(searchLower) ||
        (booking.serviceName || booking.service_name || booking.healthcare_service_name || '')?.toLowerCase().includes(searchLower) ||
        (booking.notes || booking.patient_notes || booking.service_note || '')?.toLowerCase().includes(searchLower)
      );
    }

    if (filters.status) {
      filtered = filtered.filter(booking => 
        (booking.status || booking.status_name) === filters.status
      );
    }

    if (filters.executionUnitId) {
      filtered = filtered.filter(booking => 
        (booking.executionUnitId || booking.execution_unit_id) === filters.executionUnitId
      );
    }

    if (filters.patientName) {
      filtered = filtered.filter(booking =>
        (booking.patientName || booking.person_name || '')?.toLowerCase().includes(filters.patientName.toLowerCase())
      );
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(booking =>
        new Date(booking.appointmentDate || booking.start_time || booking.date) >= new Date(filters.dateFrom)
      );
    }

    if (filters.dateTo) {
      filtered = filtered.filter(booking =>
        new Date(booking.appointmentDate || booking.start_time || booking.date) <= new Date(filters.dateTo)
      );
    }

    setFilteredBookings(filtered);
    setPage(1);
  };

  const handleFilterChange = (field: keyof BookingFilters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: '',
      executionUnitId: '',
      serviceId: '',
      dateFrom: '',
      dateTo: '',
      patientName: ''
    });
  };

  const handleSync = async () => {
    setLoading(true);
    setError(null);
    try {
      await bookingService.syncFromRecup();
      setSuccess('Sincronizzazione completata con successo');
      await loadData();
    } catch (err) {
      setError('Errore durante la sincronizzazione: ' + (err as Error).message);
    } finally {
      setLoading(false);
      setShowSyncConfirm(false);
    }
  };

  const handleCustomSync = async (options: any) => {
    setLoading(true);
    setError(null);
    setShowSyncConfig(false);
    
    try {
      const result = await bookingService.syncFromRecup(options);
      
      if (result.success) {
        setSuccess(
          `Sincronizzazione personalizzata completata! 
           Processati: ${result.processed || 0}, 
           Creati: ${result.created || 0}, 
           Aggiornati: ${result.updated || 0}, 
           Errori: ${result.errors || 0}`
        );
      } else {
        setError(result.error || 'Sincronizzazione fallita');
      }
      
      await loadData();
    } catch (err: any) {
      if (err.confirmationRequired) {
        setError(`Trovati ${err.existingCount} record esistenti. Confermare per procedere.`);
      } else {
        setError('Errore durante la sincronizzazione personalizzata: ' + (err.message || err));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (deleteOptions: DeleteOptions) => {
    if (!bookingToDelete) return;
    
    setLoading(true);
    setError(null);
    try {
      const result = await bookingService.deleteBooking(bookingToDelete.id, deleteOptions);
      
      let successMessage = result.message;
      if (result.details) {
        const { local_deleted, recup_deleted, recup_id } = result.details;
        if (deleteOptions.isHardDelete) {
          successMessage += ` (Locale: ${local_deleted ? 'OK' : 'FAIL'}, RECUP: ${recup_deleted ? 'OK' : recup_id ? 'FAIL' : 'N/A'})`;
        }
      }
      
      setSuccess(successMessage);
      await loadData();
    } catch (err) {
      const errorMessage = deleteOptions.isHardDelete 
        ? 'Errore nell\'eliminazione definitiva: ' + (err as Error).message
        : 'Errore nell\'eliminazione: ' + (err as Error).message;
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed': return 'success';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      case 'completed': return 'info';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed': return 'Confermata';
      case 'pending': return 'In Attesa';
      case 'cancelled': return 'Annullata';
      case 'completed': return 'Completata';
      default: return status;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatTime = (timeData: string | Date) => {
    if (!timeData) return '';
    
    // Se è già una stringa in formato HH:MM, restituiscila
    if (typeof timeData === 'string' && timeData.includes(':') && timeData.length <= 8) {
      return timeData.substring(0, 5);
    }
    
    // Se è un timestamp o un Date object, estrai l'ora
    try {
      const date = new Date(timeData);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('it-IT', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false 
      });
    } catch (error) {
      return '';
    }
  };

  // Paginazione
  const paginatedBookings = filteredBookings.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);

  return (
    <Box className="booking-list-container">
      {/* Header con statistiche */}
      <Box className="booking-header" mb={3}>
        <Typography variant="h4" component="h1" gutterBottom>
          Gestione Prenotazioni
        </Typography>
        
        <Grid container spacing={2} className="stats-grid">
          <Grid item xs={12} sm={6} md={2.4}>
            <Card className="stat-card">
              <CardContent>
                <Box display="flex" alignItems="center">
                  <CalendarIcon color="primary" />
                  <Box ml={1}>
                    <Typography variant="h6">{stats.total}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Totale
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card className="stat-card">
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Badge badgeContent={stats.confirmed} color="success">
                    <PersonIcon color="success" />
                  </Badge>
                  <Box ml={1}>
                    <Typography variant="h6">{stats.confirmed}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Confermate
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card className="stat-card">
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Badge badgeContent={stats.pending} color="warning">
                    <HospitalIcon color="warning" />
                  </Badge>
                  <Box ml={1}>
                    <Typography variant="h6">{stats.pending}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      In Attesa
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card className="stat-card">
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Badge badgeContent={stats.cancelled} color="error">
                    <PersonIcon color="error" />
                  </Badge>
                  <Box ml={1}>
                    <Typography variant="h6">{stats.cancelled}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Annullate
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card className="stat-card">
              <CardContent>
                <Box display="flex" alignItems="center">
                  <Badge badgeContent={stats.completed} color="info">
                    <CalendarIcon color="info" />
                  </Badge>
                  <Box ml={1}>
                    <Typography variant="h6">{stats.completed}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Completate
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Toolbar */}
      <Box className="booking-toolbar" mb={2}>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAdd}
            className="action-button"
          >
            Nuova Prenotazione
          </Button>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={() => setShowSyncConfirm(true)}
            disabled={loading}
            className="action-button"
          >
            Sincronizza RECUP
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<SyncIcon />}
            onClick={() => setShowSyncConfig(true)}
            disabled={loading}
            className="action-button"
          >
            Sync Personalizzato
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
            className="action-button"
          >
            Ricarica
          </Button>
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
            className="action-button"
          >
            Filtri
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            className="action-button"
          >
            Esporta
          </Button>
        </Box>
      </Box>

      {/* Filtri */}
      {showFilters && (
        <Card className="filters-card" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filtri di Ricerca
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Ricerca generale"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon color="action" />
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth>
                  <InputLabel>Stato</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="">Tutti</MenuItem>
                    <MenuItem value="pending">In Attesa</MenuItem>
                    <MenuItem value="confirmed">Confermata</MenuItem>
                    <MenuItem value="completed">Completata</MenuItem>
                    <MenuItem value="cancelled">Annullata</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Unità Erogante</InputLabel>
                  <Select
                    value={filters.executionUnitId}
                    onChange={(e) => handleFilterChange('executionUnitId', e.target.value)}
                  >
                    <MenuItem value="">Tutte</MenuItem>
                    {executionUnits.map((unit) => (
                      <MenuItem key={unit.id} value={unit.id}>
                        {unit.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data Da"
                  value={filters.dateFrom}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data A"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <Box mt={2}>
              <Button onClick={clearFilters} variant="outlined" size="small">
                Pulisci Filtri
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Messaggi */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Tabella prenotazioni */}
      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Paziente</TableCell>
                      <TableCell>Servizio</TableCell>
                      <TableCell>Data</TableCell>
                      <TableCell>Ora</TableCell>
                      <TableCell>Unità Erogante</TableCell>
                      <TableCell>Stato</TableCell>
                      <TableCell align="center">Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedBookings.map((booking) => (
                      <TableRow key={booking.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {booking.patientName || booking.person_name || 'N/A'}
                          </Typography>
                          {(booking.patientFiscalCode || booking.fiscal_code) && (
                            <Typography variant="caption" color="textSecondary">
                              CF: {booking.patientFiscalCode || booking.fiscal_code}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {booking.serviceName || booking.service_name || booking.healthcare_service_name || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {formatDate(booking.appointmentDate || booking.start_time || booking.date)}
                        </TableCell>
                        <TableCell>
                          {formatTime(booking.appointmentTime || booking.start_time || booking.time)}
                        </TableCell>
                        <TableCell>
                          {executionUnits.find(u => u.id === (booking.executionUnitId || booking.execution_unit_id))?.name || 
                           booking.execution_unit_name || 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getStatusLabel(booking.status || booking.status_name || 'unknown')}
                            color={getStatusColor(booking.status || booking.status_name || 'unknown') as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Visualizza dettagli">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedBooking(booking);
                                  setShowDetails(true);
                                }}
                              >
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Modifica">
                              <IconButton
                                size="small"
                                onClick={() => onEdit?.(booking)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Elimina">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setBookingToDelete(booking);
                                  setShowDeleteConfirm(true);
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Paginazione */}
              {totalPages > 1 && (
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, newPage) => setPage(newPage)}
                    color="primary"
                  />
                </Box>
              )}

              {filteredBookings.length === 0 && !loading && (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="textSecondary">
                    Nessuna prenotazione trovata
                  </Typography>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog dettagli prenotazione */}
      <Dialog
        open={showDetails}
        onClose={() => setShowDetails(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Dettagli Prenotazione</DialogTitle>
        <DialogContent>
          {selectedBooking && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Paziente</Typography>
                <Typography variant="body1">{selectedBooking.patientName || selectedBooking.person_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Codice Fiscale</Typography>
                <Typography variant="body1">{selectedBooking.patientFiscalCode || selectedBooking.fiscal_code || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Servizio</Typography>
                <Typography variant="body1">{selectedBooking.serviceName || selectedBooking.service_name || selectedBooking.healthcare_service_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Data e Ora</Typography>
                <Typography variant="body1">
                  {formatDate(selectedBooking.appointmentDate || selectedBooking.start_time || selectedBooking.date)} alle {formatTime(selectedBooking.appointmentTime || selectedBooking.start_time || selectedBooking.time)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Unità Erogante</Typography>
                <Typography variant="body1">{selectedBooking.execution_unit_name || executionUnits.find(u => u.id === (selectedBooking.executionUnitId || selectedBooking.execution_unit_id))?.name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Stato</Typography>
                <Typography variant="body1">{getStatusLabel(selectedBooking.status || selectedBooking.status_name || 'unknown')}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Telefono</Typography>
                <Typography variant="body1">{selectedBooking.patientPhone || selectedBooking.phone_number || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2">Email</Typography>
                <Typography variant="body1">{selectedBooking.patientEmail || selectedBooking.email || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Note</Typography>
                <Typography variant="body1">{selectedBooking.notes || selectedBooking.patient_notes || selectedBooking.service_note || selectedBooking.detail || 'Nessuna nota'}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetails(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog conferma eliminazione con opzione soft/hard delete */}
      <DeleteConfirmationDialog
        open={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setBookingToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Elimina Prenotazione"
        itemName={bookingToDelete ? `${bookingToDelete.patient_name || 'Paziente'} - ${new Date(bookingToDelete.appointment_datetime).toLocaleDateString()}` : ''}
        entityType="prenotazione"
        allowHardDelete={true}
        userIsAdmin={userIsAdmin}
        relatedData={{
          // In futuro si potrebbero aggiungere informazioni su elementi correlati
          bookings: 0
        }}
      />

      {/* Dialog conferma sincronizzazione */}
      <ConfirmationDialog
        open={showSyncConfirm}
        title="Conferma Sincronizzazione"
        message="Vuoi sincronizzare le prenotazioni da RECUP? Questa operazione potrebbe richiedere alcuni minuti."
        onConfirm={handleSync}
        onCancel={() => setShowSyncConfirm(false)}
        confirmText="Sincronizza"
        cancelText="Annulla"
        severity="info"
      />

      {/* Dialog configurazione sincronizzazione personalizzata */}
      <SyncConfigDialog
        open={showSyncConfig}
        onClose={() => setShowSyncConfig(false)}
        onSync={handleCustomSync}
        loading={loading}
      />
    </Box>
  );
};

export default BookingList;